// Route: /tutor/scenarios — управление scenarios (предустановленные system prompts).
// См. docs/ui-spec/tutor.scenarios.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, SectionHeader } from "@studyhub/ui";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { toast } from "@studyhub/ui/toast";
import { IconChevronLeft, IconPlus, IconSparkles } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LIST_PAGE_GRID, LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { ScenarioCard } from "@/components/tutor/scenarios/scenario-card";
import {
	ScenarioFormDialog,
	type ScenarioFormValues,
} from "@/components/tutor/scenarios/scenario-form-dialog";
import { useConfirm } from "@/hooks/use-confirm";
import { useOnlineStatus } from "@/hooks/use-online-status";
import {
	type TutorScenarioDto,
	useCreateScenario,
	useDeleteScenario,
	useTutorScenarios,
	useUpdateScenario,
} from "@/hooks/use-tutor-scenarios";

const searchSchema = z.object({
	filter: z.enum(["all", "builtin", "mine"]).optional(),
	q: z.string().optional(),
	demo: z.enum(["default", "empty"]).optional(),
});

export const Route = createFileRoute("/tutor/scenarios")({
	validateSearch: tolerantSearch(searchSchema),
	component: ScenariosPage,
});

function ScenariosPage(): React.ReactElement {
	const { t, i18n } = useTranslation(["tutor", "common"]);
	const { t: tTrash } = useTranslation("trash");
	const confirm = useConfirm();
	const online = useOnlineStatus();
	const locale = i18n.language.slice(0, 2);
	const search = Route.useSearch();
	const [q, setQ] = useState(search.q ?? "");

	const scenariosQuery = useTutorScenarios(locale);
	const createMut = useCreateScenario();
	const updateMut = useUpdateScenario();
	const deleteMut = useDeleteScenario();

	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<TutorScenarioDto | null>(null);

	const allScenarios = useMemo(() => scenariosQuery.data?.scenarios ?? [], [scenariosQuery.data]);

	const filter = search.filter ?? "all";
	const filtered = useMemo(() => {
		let items = allScenarios;
		if (filter === "builtin") items = items.filter((s) => s.isBuiltin);
		if (filter === "mine") items = items.filter((s) => !s.isBuiltin);
		if (q) {
			const query = q.toLowerCase();
			items = items.filter(
				(s) =>
					s.name.toLowerCase().includes(query) ||
					(s.description ?? "").toLowerCase().includes(query),
			);
		}
		return items;
	}, [allScenarios, filter, q]);

	// Адаптируем TutorScenarioDto → ScenarioDto (UI карточка ждёт `description: string`).
	const adapted = useMemo(
		() =>
			filtered.map((s) => ({
				id: s.id,
				isBuiltin: s.isBuiltin,
				name: s.name,
				description: s.description ?? "",
				systemPrompt: s.systemPrompt,
				locale: s.locale,
			})),
		[filtered],
	);
	const builtin = adapted.filter((s) => s.isBuiltin);
	const mine = adapted.filter((s) => !s.isBuiltin);

	function openCreate() {
		setEditing(null);
		setFormOpen(true);
	}
	function openEdit(id: string) {
		const item = allScenarios.find((s) => s.id === id);
		if (!item) return;
		setEditing(item);
		setFormOpen(true);
	}

	async function handleSubmit(values: ScenarioFormValues): Promise<void> {
		try {
			if (editing) {
				await updateMut.mutateAsync({
					id: editing.id,
					name: values.name,
					description: values.description,
					systemPrompt: values.systemPrompt,
				});
				toast.success(t("scenarios.toast.updated", { defaultValue: "Сценарий обновлён" }));
			} else {
				await createMut.mutateAsync({
					name: values.name,
					description: values.description,
					systemPrompt: values.systemPrompt,
					locale,
				});
				toast.success(t("scenarios.toast.created", { defaultValue: "Сценарий создан" }));
			}
			setFormOpen(false);
			setEditing(null);
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: t("scenarios.toast.saveFailed", { defaultValue: "Не удалось сохранить" }),
			);
		}
	}

	async function handleDelete(id: string, name: string): Promise<void> {
		if (!online) {
			toast.error(tTrash("offline.action"));
			return;
		}
		const ok = await confirm({
			title: t("scenarios.deleteConfirm.title", { defaultValue: "Удалить сценарий?" }),
			description: t("scenarios.deleteConfirm.body", { name }),
			confirmLabel: t("common:actions.delete"),
			tone: "destructive",
		});
		if (!ok) return;
		try {
			await deleteMut.mutateAsync(id);
			toast.success(t("scenarios.toast.deleted", { defaultValue: "Сценарий удалён" }));
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: t("scenarios.toast.deleteFailed", { defaultValue: "Не удалось удалить" }),
			);
		}
	}

	return (
		<div className={LIST_PAGE_SHELL}>
			{/* Заголовок секции */}
			<div className="flex items-center gap-1">
				<IconTooltip label={t("chat.back", { defaultValue: "Назад" })}>
					<Button
						variant="ghost"
						size="icon"
						asChild
						className="size-8 shrink-0 cursor-pointer"
						aria-label={t("chat.back", { defaultValue: "Назад" })}
					>
						<Link to="/tutor">
							<IconChevronLeft size={16} />
						</Link>
					</Button>
				</IconTooltip>
				<SectionHeader
					className="flex-1"
					icon={<IconSparkles />}
					category="ai"
					title={t("scenariosPage.title", { defaultValue: "Сценарии" })}
					description={t("scenariosPage.subtitle", {
						defaultValue: "Готовые системные промпты под стиль диалога",
					})}
					actions={
						<Button onClick={openCreate} className="cursor-pointer">
							<IconPlus size={16} />
							<span className="hidden sm:inline">
								{t("scenariosPage.create", { defaultValue: "Создать" })}
							</span>
						</Button>
					}
				/>
			</div>

			{/* Тулбар — унифицирован через ListToolbar: поиск + сегмент-фильтр
			    (FilterTabs all/builtin/mine в слоте segments). Без grid/list. */}
			<ListToolbar
				search={{
					value: q,
					onChange: setQ,
					placeholder: t("scenariosPage.search", { defaultValue: "Поиск..." }),
				}}
				segments={<FilterTabs current={filter} />}
				showViewToggle={false}
			/>

			{/* Builtin */}
			{builtin.length > 0 && (
				<section>
					<h2 className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
						{t("scenariosPage.builtin", {
							defaultValue: "Встроенные ({{n}})",
							n: builtin.length,
						})}
					</h2>
					<div className={LIST_PAGE_GRID}>
						{builtin.map((s) => (
							<ScenarioCard key={s.id} scenario={s} />
						))}
					</div>
				</section>
			)}

			{/* Mine */}
			<section>
				<h2 className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
					{t("scenariosPage.mine", {
						defaultValue: "Мои ({{n}})",
						n: mine.length,
					})}
				</h2>
				{mine.length > 0 ? (
					<div className={LIST_PAGE_GRID}>
						{mine.map((s) => (
							<ScenarioCard
								key={s.id}
								scenario={s}
								onEdit={() => openEdit(s.id)}
								onDelete={() => void handleDelete(s.id, s.name)}
							/>
						))}
					</div>
				) : (
					<div className="flex flex-col items-center gap-3 rounded-[var(--radius-panel)] border border-border bg-card p-8 text-center shadow-e1">
						<div className="inline-flex size-12 items-center justify-center rounded-[var(--radius-tile)] tint-indigo [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
							<IconSparkles size={22} />
						</div>
						<p className="text-sm font-semibold text-foreground">
							{t("scenariosPage.empty.title", { defaultValue: "Создай свой сценарий" })}
						</p>
						<p className="max-w-md text-xs text-muted-foreground">
							{t("scenariosPage.empty.hint", {
								defaultValue:
									"Describe a dialogue style with one system prompt and reuse it across chats.",
							})}
						</p>
						<Button variant="outline" onClick={openCreate} className="cursor-pointer">
							<IconPlus size={14} />
							{t("scenariosPage.empty.cta", { defaultValue: "Создать" })}
						</Button>
					</div>
				)}
			</section>

			{/* Create / Edit dialog */}
			<ScenarioFormDialog
				open={formOpen}
				onOpenChange={(o) => {
					setFormOpen(o);
					if (!o) setEditing(null);
				}}
				mode={editing ? "edit" : "create"}
				initialValues={
					editing
						? {
								name: editing.name,
								description: editing.description ?? "",
								systemPrompt: editing.systemPrompt,
							}
						: undefined
				}
				isSubmitting={createMut.isPending || updateMut.isPending}
				onSubmit={handleSubmit}
			/>
		</div>
	);
}

function FilterTabs({ current }: { current: "all" | "builtin" | "mine" }) {
	const { t } = useTranslation("tutor");
	const navigate = useNavigate();
	return (
		<SegmentedControl
			scrollable
			aria-label={t("scenariosPage.filters.all", { defaultValue: "Все" })}
			value={current}
			onValueChange={(filter) => void navigate({ to: "/tutor/scenarios", search: { filter } })}
			options={[
				{ value: "all", label: t("scenariosPage.filters.all", { defaultValue: "Все" }) },
				{
					value: "builtin",
					label: t("scenariosPage.filters.builtin", { defaultValue: "Встроенные" }),
				},
				{ value: "mine", label: t("scenariosPage.filters.mine", { defaultValue: "Мои" }) },
			]}
		/>
	);
}
